import { useCallback } from 'react';

import { AdjustmentsHorizontalIcon, CheckIcon } from '@heroicons/react/24/outline';

import { cls } from '@/utils/cls';

type ColumnSelectorProps<K extends string> = {
  columns: ColumnDef<K>[];
  onToggle: (key: K) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
};

export function ColumnSelector<K extends string>({ open, setOpen, columns, onToggle }: ColumnSelectorProps<K>) {
  const toggleOpen = useCallback(() => setOpen(!open), [open, setOpen]);

  return (
    <>
      <button
        type="button"
        onClick={toggleOpen}
        className="block rounded-md bg-gray-50 px-2 py-2 text-center text-sm font-semibold text-white shadow-xs hover:bg-gray-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#274483] cursor-pointer"
        aria-label="Select columns"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <AdjustmentsHorizontalIcon className="w-5 h-5 text-[#274483]" />
      </button>

      {open ? (
        <div className="absolute right-0 top-full z-50 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-y-auto">
          <div className="py-1">
            {columns.map((col) => (
              <button
                key={col.key}
                type="button"
                onClick={() => onToggle(col.key)}
                className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 flex items-center justify-between"
              >
                <span className={cls(col.visible ? 'text-gray-900' : 'text-gray-500')}>{col.label}</span>
                {col.visible ? <CheckIcon className="w-3.5 h-3.5 text-[#274483]" /> : null}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}
